import { get } from 'lodash-es';

import type {
  AnyDocument,
  DocumentRelationshipObject,
  Sorting,
} from '../../../../../../../types';

const sortDocuments = (
  documents: AnyDocument[],
  sorting: Sorting[],
): AnyDocument[] =>
  documents.sort((a, b) => {
    for (const { column, direction } of sorting) {
      const aValue = get(a, column);
      const bValue = get(b, column);

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    }
    return 0; // If all sorting fields are equal, maintain original order
  });

// TODO: @haxxxton consider adding a "nullLast" option so we can sort employment/address
// history with nulls first and periodEnd desc? (currently periodStart _could_ result in an
// incorrect order if we have a past address with a periodStart after a current address with
// no periodStart, but this is unlikely and we can just enforce that current addresses have a
// periodStart in the present/future)
export const sortDocumentRelationships = (
  document: AnyDocument,
  relationshipsObject: Record<string, DocumentRelationshipObject>,
): AnyDocument => {
  let sortedDocument = document;

  Object.entries(relationshipsObject).forEach(
    ([key, { relationships, cardinality, sorting }]) => {
      // Only sort if it's a hasMany relationship and sorting instructions are provided
      if (cardinality === 'hasMany') {
        // find the related document part by key
        const relatedDocuments = get(document, key) as
          | AnyDocument[]
          | undefined;
        if (relatedDocuments) {
          // optionally sort the related documents based on the provided sorting instructions
          const sortedRelatedDocuments = sorting
            ? sortDocuments([...relatedDocuments], sorting)
            : relatedDocuments;
          sortedDocument = {
            ...sortedDocument,
            // if we have relationships, we need to recursively sort those as well,
            // otherwise we just assign the sorted related documents
            ...(relationships
              ? {
                  [key]: sortedRelatedDocuments.map((doc) =>
                    sortDocumentRelationships(doc, relationships),
                  ),
                }
              : { [key]: sortedRelatedDocuments }),
          };
        }
      }
    },
  );

  return sortedDocument;
};
